/**
 * Dialog showing all attachments from a ticket (original + replies).
 */
import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download, FileText, ImageIcon, Film, X } from 'lucide-react';
import { BACKEND_BASE_URL } from '@/lib/apiBase';
import { formatDateTime } from './TicketComponents';

function iconFor(mime) {
  const m = (mime || '').toLowerCase();
  if (m.startsWith('image/')) return ImageIcon;
  if (m.startsWith('video/')) return Film;
  return FileText;
}

export function AllAttachmentsDialog({ open, onOpenChange, ticket }) {
  const [preview, setPreview] = useState(null);

  const all = useMemo(() => {
    if (!ticket) return [];
    const rows = [];
    (ticket.attachments || []).forEach((a) =>
      rows.push({
        ...a,
        source: 'Original ticket',
        sourceDate: ticket.created_at,
        by: ticket.created_by_name,
      })
    );
    (ticket.replies || []).forEach((r) => {
      (r.attachments || []).forEach((a) =>
        rows.push({
          ...a,
          source: `Reply`,
          sourceDate: r.created_at,
          by: r.user_name,
        })
      );
    });
    return rows;
  }, [ticket]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="all-attachments-dialog">
        <DialogHeader>
          <DialogTitle>All Attachments ({all.length})</DialogTitle>
        </DialogHeader>

        {all.length === 0 ? (
          <div className="py-12 text-center text-slate-500">No attachments on this ticket.</div>
        ) : (
          <div className="flex-1 overflow-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 py-2">
            {all.map((att, idx) => {
              const url = `${BACKEND_BASE_URL}${att.url}`;
              const mt = (att.mime_type || '').toLowerCase();
              const Icon = iconFor(mt);
              const isImage = mt.startsWith('image/');
              return (
                <div
                  key={`${att.id || idx}-${idx}`}
                  className="rounded-lg border border-slate-200 bg-white p-2 flex flex-col"
                  data-testid={`all-attachment-item`}
                >
                  <div className="aspect-video bg-slate-50 rounded overflow-hidden flex items-center justify-center mb-2">
                    {isImage ? (
                      <img
                        src={url}
                        alt={att.filename}
                        className="w-full h-full object-cover cursor-zoom-in"
                        onClick={() => setPreview({ src: url, name: att.filename })}
                      />
                    ) : (
                      <Icon className="w-10 h-10 text-slate-400" />
                    )}
                  </div>
                  <div className="text-sm font-medium text-slate-800 truncate" title={att.filename}>
                    {att.filename}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {att.source} · {att.by || '—'}
                  </div>
                  <div className="text-[11px] text-slate-400 mb-2">
                    {att.sourceDate ? formatDateTime(att.sourceDate) : ''}
                  </div>
                  <div className="mt-auto flex items-center gap-2">
                    <a
                      href={url}
                      download={att.filename}
                      className="flex-1 inline-flex items-center justify-center gap-1 text-xs bg-slate-900 text-white rounded px-2 py-1.5 hover:bg-slate-800 transition-colors"
                      data-testid="attachment-download-link"
                    >
                      <Download className="w-3 h-3" /> Download
                    </a>
                    {!isImage && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-1 text-xs bg-slate-100 text-slate-700 rounded px-2 py-1.5 hover:bg-slate-200 transition-colors"
                      >
                        Open
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {preview && (
          <div
            className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center"
            onClick={() => setPreview(null)}
          >
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-1.5 rounded-lg"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <img
              src={preview.src}
              alt={preview.name}
              className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
